import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  effect,
  inject,
  signal
} from '@angular/core';
import {
  ActivatedRoute,
  PRIMARY_OUTLET,
  Router,
  UrlSerializer
} from '@angular/router';

import { AuthService } from '../services/auth.service';

const SESSION_RETRY_INTERVAL_MS = 15000;

/** The query param that carries the URL the user was headed to. */
export const SESSION_UNAVAILABLE_RETURN_PARAM = 'returnUrl';

/**
 * Shown while the API cannot say who is signed in. It keeps asking, and once
 * the API answers it navigates back to where the user was headed, inside the
 * app: a reload would lose a Connect link's token, which lives only in memory.
 * The guards then decide as usual, so a session continues and a 401 goes to
 * /login.
 */
@Component({
  selector: 'app-session-unavailable',
  templateUrl: './session-unavailable.component.html',
  styleUrls: ['./session-unavailable.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SessionUnavailableComponent implements AfterViewInit {
  @ViewChild('title') private title?: ElementRef<HTMLElement>;

  protected readonly checking = signal(false);

  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly urlSerializer = inject(UrlSerializer);

  constructor() {
    effect(() => {
      if (!this.authService.sessionUnavailable()) {
        void this.leave();
      }
    });

    const timer = setInterval(
      () => void this.retry(),
      SESSION_RETRY_INTERVAL_MS
    );
    const onOnline = (): void => void this.retry();
    window.addEventListener('online', onOnline);
    inject(DestroyRef).onDestroy(() => {
      clearInterval(timer);
      window.removeEventListener('online', onOnline);
    });
  }

  public ngAfterViewInit(): void {
    this.title?.nativeElement.focus();
  }

  public async retry(): Promise<void> {
    if (this.checking()) {
      return;
    }
    // The session is already known, so only the way back failed: try it again.
    if (!this.authService.sessionUnavailable()) {
      await this.leave();
      return;
    }
    this.checking.set(true);
    try {
      await this.authService.retrySessionCheck();
    } finally {
      this.checking.set(false);
    }
  }

  /**
   * Back to the return target, or to `/` when that navigation fails or a guard
   * refuses it, so a broken target never strands the user on this page.
   */
  private async leave(): Promise<void> {
    const target = this.returnTarget();
    const left = await this.router
      .navigateByUrl(target, { replaceUrl: true })
      .catch(() => false);
    if (!left && target !== '/') {
      await this.router
        .navigateByUrl('/', { replaceUrl: true })
        .catch(() => false);
    }
  }

  private returnTarget(): string {
    const target = this.route.snapshot.queryParamMap.get(
      SESSION_UNAVAILABLE_RETURN_PARAM
    );
    if (!isReturnPath(target)) {
      return '/';
    }
    // The router decodes segments and ignores matrix params, so a
    // percent-encoded `/unavailable` or `/unavailable;x=1` is this page too:
    // compare the parsed first segment, not the text.
    try {
      const first =
        this.urlSerializer.parse(target).root.children[PRIMARY_OUTLET]
          ?.segments[0]?.path;
      return first === 'unavailable' ? '/' : target;
    } catch {
      return '/';
    }
  }
}

/**
 * An in-app path to go back to. Unlike a post-login target, `/` and `/login`
 * are fine here: the user may have been headed to either. Anything that could
 * leave the app is not.
 */
function isReturnPath(target: string | null): target is string {
  if (!target?.startsWith('/')) {
    return false;
  }
  if (target.startsWith('//') || target.startsWith('/\\')) {
    return false;
  }
  return !target.includes('://') && ![...target].some(isControlOrSpace);
}

function isControlOrSpace(character: string): boolean {
  const code = character.charCodeAt(0);
  return code <= 0x20 || code === 0x7f;
}
